/**
 * The OverlayFlyout component is used to display content in the OverlayFlyout section
 *
 * @module views/components/OverlayFlyout
 * @memberof -Common
 */
import React, { forwardRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { isServer } from '../../../utils/device_detection/device_detection';
import './OverlayFlyout.scss';
import Icon from '../../components/Icon/Icon';
import PropTypes from 'prop-types';

/**
 * Represents a OverlayFlyout component
 *
 * @method
 * @param {object} props - React properties passed from composition
 * @returns OverlayFlyout
 */

const OverlayFlyout = forwardRef( ( props, ref ) => {
  const {
    children,
    alignment,
    closeAccessibilityAction,
    crossButtonVisibility,
    customClassName
  } = props;

  if( isServer() ){
    return null;
  }

  const component =     (
    <dialog
      ref={ ref }
      className={ classNames( `${OVERLAY_FLYOUT_CLASS} ${OVERLAY_FLYOUT_CLASS}--container`, {
        ...( alignment && { [`${OVERLAY_FLYOUT_CLASS}--${alignment}`]: alignment } ),
        ...( customClassName && { [`${customClassName}`]: customClassName } )
      } ) }
    >
      { children }
      <form method='dialog'>
        { crossButtonVisibility && (
          <button className={ `${OVERLAY_FLYOUT_CLASS}__close` }
            aria-label={ closeAccessibilityAction?.label }
            type='submit'
          >
            <Icon size='s'
              name={ 'X' }
            />
          </button>
        ) }
      </form>
    </dialog>
  )

  return createPortal( component, document.body );
} );

/**
 * Overlay Flyout class
 * @constant {string}
 */
export const OVERLAY_FLYOUT_CLASS = 'OverlayFlyout'

/**
 * Property type definitions
 * @type {object}
 * @property {object} closeAccessibilityAction - Sets close accessibility label and graphql
 */
export const propTypes = {
  /** Sets close accessibility label and graphql */
  closeAccessibilityAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } )
};

OverlayFlyout.propTypes = propTypes;

export default OverlayFlyout;
